import type {
  AdmissionApplication, AdmissionCycle, ApplicationSource, AssessmentPathway, GuardianRelation, IdNamePair, PendingGuardianCapture,
} from '../types/admissions';
import { statusLabel } from '../components/common/StatusChip';
import { RECOMMENDATION_LABEL } from '../components/admissions/dialogs/InterviewRoundDialog';
import { concessionTypeLabel } from '../components/admissions/dialogs/ConcessionRequestDialog';
import { DOCUMENT_STATUS } from '../components/admissions/drawer/CandidateDocumentsTab';
import { fieldDisplayValue, isSystemFieldKey } from '../components/admissions/form/admissionFormFields';
import { assessmentPlan, documentsAcrossPathways, isScored, pathwayFor, testOutcome } from './admissionAssessment';
import dateTimeUtils from './dateTimeUtils';
import { fmtClassName, fmtCnic } from './pdf/format';

/**
 * Admission application records — the one place that turns an application
 * into the values staff read: application number, GR number, class, source, and the export
 * columns. The applications table, the Excel export and the PDF export all
 * read from here, so an exported file always says what the screen says.
 */

/** Set once the applicant enrolls — or already, for a returning student. */
export const grNumberOf = (app: Pick<AdmissionApplication, 'personId'>): string =>
  typeof app.personId === 'object' ? app.personId?.grNumber ?? '' : '';

export const classNameOf = (app: Pick<AdmissionApplication, 'classId'>): string =>
  typeof app.classId === 'object' ? app.classId?.name ?? '' : '';

/** When and where a candidate sits the test — their class's row in the
 *  cycle's test schedule. The backend's `testSittingFor` mirrors this. */
export const testSittingOf = (cycle: { testSchedule?: AdmissionCycle['testSchedule'] }, app: Pick<AdmissionApplication, 'classId'>) => {
  const classId = typeof app.classId === 'object' ? app.classId?._id : app.classId;
  const row = (cycle.testSchedule ?? []).find(r => r.classId === classId);
  return { testDate: row?.date || null, testTime: row?.reportingTime || null, testVenue: row?.venue || null };
};

/** How the application reached the school, in staff language. */
export const APPLICATION_SOURCE_LABEL: Record<ApplicationSource, string> = {
  public_link: 'Online form',
  web_form: 'Website',
  walk_in: 'Walk-in',
  imported: 'Bulk upload',
  referral: 'Referral',
  form_link: 'Form link',
};

export type ExportValue = string | number | null;

/** Column headers for marks — shared by the export and the marks upload
 *  sheet, so an exported file can be filled in and uploaded back. */
export const marksHeader = {
  /** The column marks rows are matched on. */
  applicationNumber: 'Application no.',
  paper: (p: { subject: string; maxMarks: number }) => `${p.subject} (of ${p.maxMarks})`,
  round: (r: { name: string; maxMarks: number }) => `${r.name} (of ${r.maxMarks})`,
  criterion: (r: { name: string }, c: { criterion: string; maxMarks: number }) => `${r.name} – ${c.criterion} (of ${c.maxMarks})`,
  /** An unscored question's answer column (choice or note). */
  answer: (r: { name: string }, c: { criterion: string }) => `${r.name} – ${c.criterion}`,
  recommendation: (r: { name: string }) => `${r.name} – recommendation`,
  interviewer: (r: { name: string }) => `${r.name} – interviewer`,
  comments: (r: { name: string }) => `${r.name} – comments`,
  custom: (c: { label: string; maxMarks: number }) => `${c.label} (of ${c.maxMarks})`,
};

/** One column of a list's export — Excel always, the PDF when `pdf` says how wide. */
export interface ExportColumn<Row> {
  header: string;
  /** Excel column width. */
  width: number;
  value: (row: Row) => ExportValue;
  /** Shown in the PDF too (landscape page — only the columns that fit). */
  pdf?: { flex?: number; width?: number; align?: 'left' | 'right' | 'center' };
  /** The status column — the PDF draws it as a status pill. */
  isStatus?: boolean;
}

export type ApplicationExportColumn = ExportColumn<AdmissionApplication>;

// ─── Value helpers ──────────────────────────────────────────────────────────

const date = (iso?: string | null): string => (iso ? dateTimeUtils.formatDateForDisplay(iso) : '');
const round = (n: number | null | undefined, digits: number): number | null =>
  n == null ? null : Math.round(n * 10 ** digits) / 10 ** digits;
const yesNo = (b: boolean | null | undefined): string => (b == null ? '' : b ? 'Yes' : 'No');
const idOf = (ref: string | IdNamePair | null | undefined): string =>
  !ref ? '' : typeof ref === 'object' ? ref._id : ref;
const cnic = (v?: string | null): string => (v ? fmtCnic(v) : '');
const capital = (s?: string | null): string => (s ? s[0].toUpperCase() + s.slice(1) : '');

/** Guardians as captured on the application, or — once the family is
 *  linked — as recorded on the family. */
const guardianOf = (app: AdmissionApplication, relation: GuardianRelation): PendingGuardianCapture | undefined =>
  (app.pendingGuardians?.length ? app.pendingGuardians : app.guardians ?? []).find(g => g.relation === relation);

const guardianColumns = (
  title: string,
  relation: GuardianRelation,
  fallback?: (a: AdmissionApplication) => Partial<PendingGuardianCapture>,
): ApplicationExportColumn[] => {
  const g = (a: AdmissionApplication): Partial<PendingGuardianCapture> => ({ ...fallback?.(a), ...guardianOf(a, relation) });
  return [
    { header: `${title}'s name`, width: 24, value: a => g(a).name || '' },
    { header: `${title}'s CNIC`, width: 17, value: a => cnic(g(a).cnic) },
    { header: `${title}'s phone`, width: 16, value: a => g(a).phone || '' },
    { header: `${title}'s email`, width: 24, value: a => g(a).email || '' },
    { header: `${title}'s occupation`, width: 20, value: a => g(a).occupation || '' },
  ];
};

/**
 * Every exported column, in order. Columns for documents, test papers,
 * interview rounds, custom criteria and custom form fields come from the
 * cycle's own settings, so each cycle's export carries exactly what that
 * cycle collects. `users` maps staff ids to names for the "who" columns.
 * The parent's private status link is deliberately left out.
 */
export function applicationExportColumns(cycle: AdmissionCycle, users: Record<string, string> = {}): ApplicationExportColumn[] {
  const who = (ref: string | IdNamePair | null | undefined): string =>
    (ref && typeof ref === 'object' && ref.name) || users[idOf(ref)] || '';
  const doneBy = (on?: string | null, by?: string | IdNamePair | null): string =>
    (on ? [date(on), who(by)].filter(Boolean).join(' · ') : '');

  const customFields = (cycle.formTemplate?.sections ?? [])
    .flatMap(s => s.fields ?? [])
    .filter(f => f.enabled !== false && !isSystemFieldKey(f.key));
  const termLabel = new Map((cycle.offerTerms ?? []).map(t => [t.key, t.label]));

  /** When, by whom and why the application reached `status` — from the
   *  status history, which every move writes. */
  const reached = (a: AdmissionApplication, status: AdmissionApplication['status']) =>
    [...(a.timeline ?? [])].reverse().find(t => t.to === status);
  const lastEntered = <T extends { enteredAt?: string | null; enteredBy?: string | IdNamePair | null }>(rows: T[]): T | undefined =>
    [...rows].sort((x, y) => String(y.enteredAt ?? '').localeCompare(String(x.enteredAt ?? '')))[0];

  // Marks come from each applicant's pathway. With several pathways each
  // gets its own columns ("Junior · Parent interview"), filled only for that
  // pathway's applicants.
  const multiple = (cycle.pathways ?? []).length > 1;
  // "Test date" while every assessed class sits a test; otherwise the day is an assessment.
  const assessed = (cycle.pathways ?? []).filter(p => assessmentPlan(p).hasAssessment);
  const dayHeader = assessed.every(p => p.stages?.testEnabled) ? 'Test date' : 'Assessment date';
  const pathwayColumns = (pathway: AssessmentPathway): ApplicationExportColumn[] => {
    const plan = assessmentPlan(pathway);
    const test = (a: AdmissionApplication) => testOutcome(a, plan);
    const on = (a: AdmissionApplication) => !multiple || pathwayFor(cycle, a.classId).key === pathway.key;
    const columns: ApplicationExportColumn[] = [
      // ── Entrance test (one column per paper) ──
      ...(plan.test ? [
        ...plan.test.papers.map<ApplicationExportColumn>(p => ({
          header: marksHeader.paper(p),
          width: Math.max(12, p.subject.length + 9),
          value: a => { const s = (a.testScores ?? []).find(x => x.paperKey === p.key); return !s ? null : s.absent ? 'Absent' : s.obtained; },
        })),
        { header: `Test total (of ${plan.test.maxMarks})`, width: 16, value: (a: AdmissionApplication) => test(a)?.obtained ?? null },
        { header: 'Test result', width: 11, value: (a: AdmissionApplication) => { const t = test(a); return t ? (t.passed ? 'Passed' : 'Failed') : ''; } },
        { header: 'Test marked', width: 24, value: (a: AdmissionApplication) => { const s = lastEntered(a.testScores ?? []); return s ? doneBy(s.enteredAt, s.enteredBy) : ''; } },
      ] : []),

      // ── Interview (one group per round) ──
      ...(plan.interview?.rounds ?? []).flatMap<ApplicationExportColumn>(r => {
        const sc = (a: AdmissionApplication) => (a.interviewRounds ?? []).find(x => x.roundKey === r.key);
        return [
          { header: marksHeader.round(r), width: Math.max(14, r.name.length + 9), value: a => sc(a)?.totalMarks ?? null },
          ...r.rubric.map<ApplicationExportColumn>(c => {
            const answer = (a: AdmissionApplication) => sc(a)?.rubricScores?.find(x => x.criterion === c.criterion);
            return isScored(c)
              ? { header: marksHeader.criterion(r, c), width: 18, value: a => answer(a)?.obtained ?? null }
              : { header: marksHeader.answer(r, c), width: c.kind === 'note' ? 30 : 18, value: a => answer(a)?.answer ?? '' };
          }),
          { header: marksHeader.recommendation(r), width: 20, value: a => { const rec = sc(a)?.recommendation; return rec ? RECOMMENDATION_LABEL[rec] : ''; } },
          { header: marksHeader.interviewer(r), width: 18, value: a => sc(a)?.interviewer ?? '' },
          { header: marksHeader.comments(r), width: 30, value: a => sc(a)?.comments ?? '' },
          { header: `${r.name} – marked`, width: 24, value: a => doneBy(sc(a)?.enteredAt, sc(a)?.enteredBy) },
        ];
      }),

      // ── Additional criteria ──
      ...(plan.custom ? [
        ...plan.custom.criteria.map<ApplicationExportColumn>(c => ({
          header: marksHeader.custom(c),
          width: Math.max(12, c.label.length + 9),
          value: a => a.customScores?.[c.key] ?? null,
        })),
        { header: 'Additional criteria total', width: 14, value: (a: AdmissionApplication) => a.totalCustomMarks ?? null },
      ] : []),
    ];
    return columns.map(col => ({
      ...col,
      header: multiple ? `${pathway.name} · ${col.header}` : col.header,
      value: (a: AdmissionApplication) => (on(a) ? col.value(a) : null),
    }));
  };

  const father = guardianColumns('Father', 'father', a => ({ name: a.fatherName, phone: a.fatherPhone }));
  father[0] = { ...father[0], pdf: { flex: 1.8 } };
  father[2] = { ...father[2], pdf: { width: 82 } };

  return [
    // ── Applicant ──
    { header: marksHeader.applicationNumber, width: 16, value: a => a.applicationNumber, pdf: { width: 82 } },
    { header: 'GR', width: 10, value: grNumberOf },
    { header: 'Applicant', width: 26, value: a => a.name, pdf: { flex: 2 } },
    { header: 'Gender', width: 9, value: a => capital(a.gender) },
    { header: 'Date of birth', width: 14, value: a => date(a.dob) },
    { header: 'B-Form / CNIC', width: 17, value: a => cnic(a.cnic) },
    { header: "Applicant's phone", width: 16, value: a => a.candidateContact ?? '' },
    { header: 'Email', width: 24, value: a => a.email ?? '' },
    { header: 'Address', width: 36, value: a => a.address ?? '' },

    // ── Admission ──
    { header: 'Cycle', width: 22, value: () => cycle.name },
    { header: 'Class', width: 12, value: a => fmtClassName(classNameOf(a)), pdf: { width: 58 } },
    { header: 'Status', width: 22, value: a => statusLabel({ kind: 'application', value: a.status }), pdf: { width: 104 }, isStatus: true },
    { header: 'Source', width: 14, value: a => APPLICATION_SOURCE_LABEL[a.source] ?? a.source },
    { header: 'Submitted', width: 14, value: a => date(a.createdAt), pdf: { width: 76 } },
    { header: 'Entered by', width: 20, value: a => (a.createdBy ? who(a.createdBy) : 'Parent (online)') },
    { header: 'Last updated', width: 14, value: a => date(a.updatedAt) },
    { header: 'Special case', width: 12, value: a => yesNo(a.isSpecialCase) },
    { header: 'Previous school', width: 24, value: a => a.previousSchool ?? '' },
    { header: 'Previous %', width: 11, value: a => a.previousPercentage ?? null },

    // ── Family ──
    { header: 'Family', width: 22, value: a => (typeof a.familyId === 'object' ? a.familyId?.displayName ?? '' : '') },
    { header: 'Family linked', width: 22, value: a => doneBy(a.familyResolvedAt, a.familyResolvedBy) },
    ...father,
    ...guardianColumns('Mother', 'mother'),
    ...guardianColumns('Guardian', 'guardian'),

    // ── Answers to the school's own form questions ──
    ...customFields.map<ApplicationExportColumn>(f => ({
      header: f.label,
      width: Math.max(14, Math.min(30, f.label.length + 4)),
      value: a => fieldDisplayValue(f, a.formData?.[f.key]),
    })),

    // ── Eligibility ──
    { header: 'Eligibility problems', width: 36, value: a => (a.eligibilityFlags ?? []).filter(x => x.severity === 'fail').map(x => x.message).join('; ') },
    { header: 'Eligibility warnings', width: 36, value: a => (a.eligibilityFlags ?? []).filter(x => x.severity !== 'fail').map(x => x.message).join('; ') },
    { header: 'Eligibility waived', width: 30, value: a => (a.eligibilityWaiver ? `${date(a.eligibilityWaiver.at)} · ${a.eligibilityWaiver.reason}` : '') },

    // ── Admit card ──
    ...((cycle.pathways ?? []).some(p => p.stages?.admitCardEnabled) ? [
      { header: 'Roll number', width: 16, value: (a: AdmissionApplication) => a.admitCard?.rollNumber ?? '' },
      { header: 'Admit card issued', width: 16, value: (a: AdmissionApplication) => date(a.admitCard?.issuedAt) },
      { header: dayHeader, width: 14, value: (a: AdmissionApplication) => date(testSittingOf(cycle, a).testDate) },
      { header: 'Reporting time', width: 12, value: (a: AdmissionApplication) => { const t = testSittingOf(cycle, a).testTime; return t ? dateTimeUtils.formatClockTime(t) : ''; } },
      { header: 'Venue', width: 20, value: (a: AdmissionApplication) => testSittingOf(cycle, a).testVenue ?? '' },
    ] : []),

    // ── Documents — one group per document, whichever pathways ask for it ──
    ...documentsAcrossPathways(cycle).flatMap<ApplicationExportColumn>(({ item, on, tag }) => {
      const asked = (a: AdmissionApplication) => !multiple || on.includes(pathwayFor(cycle, a.classId).name);
      const doc = (a: AdmissionApplication) => (a.documents ?? []).find(d => d.key === item.key);
      const note = tag === 'If applicable' ? '' : ` (${tag.charAt(0).toLowerCase()}${tag.slice(1)})`;
      const cols: ApplicationExportColumn[] = [
        { header: `${item.label}${note}`, width: 16, value: a => DOCUMENT_STATUS[doc(a)?.status ?? 'missing']?.label ?? '' },
        { header: `${item.label} – file`, width: 22, value: a => doc(a)?.fileName ?? '' },
        { header: `${item.label} – uploaded`, width: 14, value: a => date(doc(a)?.uploadedAt) },
        { header: `${item.label} – checked`, width: 24, value: a => { const d = doc(a); return d && (d.status === 'verified' || d.status === 'rejected') ? doneBy(d.verifiedAt, d.verifiedBy) : ''; } },
        { header: `${item.label} – originals seen`, width: 24, value: a => doneBy(doc(a)?.physicallyVerifiedAt, doc(a)?.physicallyVerifiedBy) },
        { header: `${item.label} – rejection reason`, width: 28, value: a => (doc(a)?.status === 'rejected' ? doc(a)?.rejectedReason ?? '' : '') },
      ];
      // Blank, not "Not uploaded", for applicants whose class isn't asked for it.
      return cols.map(c => ({ ...c, value: (a: AdmissionApplication) => (asked(a) ? c.value(a) : null) }));
    }),

    ...(cycle.pathways ?? []).flatMap(pathwayColumns),

    // ── Merit ──
    { header: 'Merit score', width: 12, value: a => round(a.computedMeritScore, 2), pdf: { width: 58, align: 'right' } },
    { header: 'Merit rank', width: 11, value: a => a.meritRank ?? null, pdf: { width: 44, align: 'right' } },
    { header: 'Not ranked because', width: 28, value: a => ((a.meritGaps ?? []).length ? `Missing ${a.meritGaps!.join(', ')}` : '') },

    // ── Decision ──
    {
      header: 'Exception requests', width: 40,
      value: a => (a.pendingDecisionOverrides ?? []).map(o =>
        `${statusLabel({ kind: 'application', value: o.proposedState })} requested by ${who(o.proposedBy) || 'staff'} on ${date(o.proposedAt)} (${o.rationale}) – ${o.status}`
        + (o.decidedAt ? ` by ${who(o.decidedBy) || 'staff'} on ${date(o.decidedAt)}` : '')).join('; '),
    },
    { header: 'Waitlist position', width: 12, value: a => a.waitlist?.rank ?? null },
    { header: 'Waitlisted on', width: 14, value: a => date(a.waitlist?.enteredAt) },
    { header: 'Promoted from waitlist', width: 16, value: a => date(a.waitlist?.promotedAt) },
    { header: 'Rejected', width: 24, value: a => doneBy(reached(a, 'Rejected')?.at, reached(a, 'Rejected')?.by) },
    { header: 'Rejection reason', width: 30, value: a => reached(a, 'Rejected')?.reason ?? '' },
    { header: 'Closed as not admitted', width: 24, value: a => doneBy(reached(a, 'NotAdmitted')?.at, reached(a, 'NotAdmitted')?.by) },

    // ── Offer ──
    { header: 'Offer made', width: 24, value: a => doneBy(a.offer?.issuedAt, a.offer?.issuedBy) },
    { header: 'Offer deadline', width: 14, value: a => date(a.offer?.expiresAt) },
    { header: 'Offer extensions', width: 11, value: a => (a.offer ? (a.timeline ?? []).filter(t => t.event === 'offer_extended').length : null) },
    { header: 'Family response', width: 14, value: a => capital(a.offer?.response) },
    { header: 'Responded on', width: 14, value: a => date(a.offer?.respondedAt) },
    { header: 'Decline reason', width: 28, value: a => a.offer?.declineReason ?? '' },
    { header: 'Terms accepted', width: 30, value: a => (a.offer?.termsAccepted ?? []).map(t => termLabel.get(t.termKey) ?? t.termKey).join(', ') },
    { header: 'Seat confirmed', width: 24, value: a => doneBy(reached(a, 'Confirmed')?.at, reached(a, 'Confirmed')?.by) },

    // ── Fees ──
    { header: 'Challans issued', width: 11, value: a => a.feeInvoiceIds?.length ?? 0 },
    {
      header: 'Concessions', width: 44,
      value: a => (a.concessionRequests ?? []).map(c =>
        `${concessionTypeLabel(c.type)} ${c.mode === 'percent' ? `${c.value}%` : `Rs ${c.value.toLocaleString('en-PK')}`} (${c.carryToFees ? 'monthly tuition' : 'admission fee'}) – ${c.status}`
        + (c.approvedAt ? ` by ${who(c.approvedBy) || 'staff'} on ${date(c.approvedAt)}` : '')
        + (c.reason ? `: ${c.reason}` : '')).join('; '),
    },

    // ── Withdrawal ──
    { header: 'Withdrawn', width: 24, value: a => doneBy(a.withdrawal?.at, a.withdrawal?.by) },
    { header: 'Withdrawal reason', width: 30, value: a => a.withdrawal?.reason ?? '' },
    { header: 'Refund amount', width: 12, value: a => a.withdrawal?.refundComputedAmount ?? null },
    { header: 'Refund issued', width: 24, value: a => doneBy(a.withdrawal?.refundIssuedAt, a.withdrawal?.refundIssuedBy) },
    { header: 'Refund reference', width: 16, value: a => a.withdrawal?.refundReference ?? '' },

    // ── Enrollment ──
    { header: 'Enrollment details confirmed', width: 24, value: a => doneBy(a.enrollmentConfirmation?.confirmedAt, a.enrollmentConfirmation?.confirmedBy) },
    { header: 'Enrolled', width: 24, value: a => doneBy(reached(a, 'Enrolled')?.at, reached(a, 'Enrolled')?.by) },

    // ── History ──
    {
      header: 'Status history', width: 60,
      value: a => (a.timeline ?? []).filter(t => t.to).map(t =>
        `${date(t.at)} ${statusLabel({ kind: 'application', value: t.to! })}${t.reason ? ` (${t.reason})` : ''}`).join('; '),
    },
  ];
}
